import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, ShoppingCart } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MonthlySpecialPopup() {
  const { lang } = useLanguage();
  const { addItem } = useCart();
  const [special, setSpecial] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('belgo_monthly_dismissed');
    if (dismissed) return;
    axios.get(`${API}/settings/monthly-special`).then(r => {
      if (r.data && r.data.active) {
        setSpecial(r.data);
        setTimeout(() => setShow(true), 1500);
      }
    }).catch(() => {});
  }, []);

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('belgo_monthly_dismissed', '1');
  };

  const handleAddToCart = () => {
    if (special) {
      addItem({
        id: 'monthly-special',
        name_fr: special.name_fr,
        name_en: special.name_en,
        price: special.price,
        image_url: special.image_url,
        description_fr: special.description_fr,
        description_en: special.description_en,
      });
      handleDismiss();
    }
  };

  if (!special) return null;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
            className="fixed inset-0 z-[60] bg-black/60"
          />
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 30 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed inset-0 z-[61] flex items-center justify-center p-4"
            data-testid="monthly-special-popup"
          >
            <div className="relative bg-black text-white rounded-2xl overflow-hidden max-w-lg w-full shadow-2xl">
              {/* Close */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="close-monthly-popup"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img src={special.image_url} alt={lang === 'fr' ? special.name_fr : special.name_en} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-4 left-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-5 h-5 text-[#f4d00d] fill-[#f4d00d]" />
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#f4d00d]">
                      {lang === 'fr' ? 'Poutine du mois' : 'Poutine of the Month'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-300">{lang === 'fr' ? special.month_fr : special.month_en}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-2xl font-bold mb-2">
                  {lang === 'fr' ? special.name_fr : special.name_en}
                </h3>
                <p className="text-sm text-gray-300 mb-4">
                  {lang === 'fr' ? special.description_fr : special.description_en}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#f4d00d]">{special.price?.toFixed(2)} $</span>
                  <button
                    onClick={handleAddToCart}
                    className="btn-belgo flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold bg-[#e53a3a] hover:bg-[#c62828] text-white transition-colors"
                    data-testid="add-monthly-special"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {lang === 'fr' ? 'Commander' : 'Order Now'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
